/* eslint-disable no-nested-ternary */
import { Button, Checkbox, Col, Form, Input, Modal, Progress, Row, Upload } from 'antd';
import dynamic from 'next/dynamic';
import { any, array, func, number, string } from 'prop-types';

import { CloseModalSVG } from 'components/iconsSVG/close';
import { UploadTakebackSVG, AddMoreSVG } from 'components/iconsSVG/takeback';
import { Fragment, useEffect, useState } from 'react';

import { checkProfile } from 'actions/general';
import { getDroppointsInfo, getTakebackCredit, postTakeback } from 'actions/products';
import HeadSEO from 'components/base/headSEO';
import FormInput from 'components/formInput';
import Layout from 'components/layout';
import ProtectionDataModal from 'components/protectionDataModal';
import { useI18n } from 'context/i18n';
import { useIsMobile, useLocale, useValidate } from 'hooks';
import { initCMS } from 'utils/cms';
import { COOKIES, getCookie, getLang, getMarket } from 'utils/helpers';

import { DropoffPointButton } from 'components/myAccount/returnItems';

import { RETURN_TO_DROPOFF, RETURN_TO_STORE } from 'utils/constants/shippings';
import { IS_CAMPER } from 'utils/constants/system';
import { getDomainValue } from 'utils/domain';

import { TakebackTermsAndConditionsText } from 'components/legalTexts';
import styles from './style.module.css';

const baseUrl = process.env.INTEGRA_DOMAIN;
const DispoModal = dynamic(() => import('components/dispoModal'));
const { TextArea } = Input;

const PAIRS_LIMIT = 5;
const RETURN_IN_STORE_DISABLED = ['DK', 'SE', 'CZ', 'SK', 'SI', 'EE', 'LV', 'LT', 'LU', 'FI', 'PT'];
const RETURN_IN_DROPOFF_DISABLED = ['GR'];

export const TakebackIconButton = ({ src, alt, text, xs, md, value, active, setter, index, id, groupId, children }) => {
  const classNames = [styles.icon, active === value ? styles.iconActive : ''];
  return (
    <Col
      xs={xs}
      md={md}
      className={classNames.join(' ')}
      onClick={() =>
        setter({
          id,
          groupId,
          value,
          index,
        })
      }
    >
      <img className={styles.iconOutline} src={src} alt={alt} />
      <img className={styles.iconFill} src={src.replace('outline', 'fill')} alt={`${alt} 2`} />
      <span className={styles.iconText}>{text}</span>
      {children}
    </Col>
  );
};

TakebackIconButton.propTypes = {
  src: string,
  alt: string,
  text: string,
  xs: number,
  md: number,
  value: any,
  active: any,
  setter: func,
  index: number,
  id: string,
  groupId: string,
  children: any,
};

TakebackIconButton.defaultProps = {
  xs: 24,
};

export const TakebackSelectGroup = ({ buttons, title, maxColsXs, maxColsMd, active, setter, index, id, className }) => (
  <div className={`${styles.group} ${className}`}>
    <p className={styles.groupTitle}>{title}</p>
    <Row className={styles.iconRow}>
      {buttons.map((button) => (
        <TakebackIconButton
          key={button.value}
          src={button.src}
          alt={button.text}
          text={button.text}
          xs={maxColsXs ? 24 / maxColsXs : 24}
          md={maxColsMd ? 24 / maxColsMd : 8}
          value={button.value}
          active={active}
          setter={setter}
          groupId={id}
          id={button.id}
          index={index}
        />
      ))}
    </Row>
  </div>
);

TakebackSelectGroup.propTypes = {
  title: string,
  buttons: array,
  maxColsXs: number,
  maxColsMd: number,
  active: any,
  setter: func,
  index: number,
  id: string,
  className: string,
};

TakebackSelectGroup.defaultProps = {
  className: '',
};

function TakebackModalTrigger({ count, onClick }) {
  const { t } = useI18n();
  if (!count || count === 0) return null;
  return (
    <button className={styles.modalTrigger} onClick={onClick}>
      <span>{t('takeback', 'your.products')}</span>
      <span className={styles.triggerCircle}>{count}</span>
    </button>
  );
}

TakebackModalTrigger.propTypes = {
  count: number,
  onClick: func,
};

TakebackModalTrigger.displayName = 'TakebackModalTrigger';

const STEPS = {
  PAIR_NUMBERS: 1,
  CHOOSE_DEPT_AND_PRODUCT: 2,
  CHOOSE_PRODUCT: 2.1,
  CHOOSE_ADD_OR_CHECKOUT: 3,
  CHECKOUT_FORM: 4,
  CHOOSE_RETURN_METHOD: 5,
};
function TakebackPage({ defaultProps, store, droppointUrl }) {
  const { t, profileData } = useI18n();
  const { locale, country } = useLocale();
  const validate = useValidate();
  const [currentStep, setCurrentStep] = useState(STEPS.PAIR_NUMBERS);
  const [pairNumbers, setPairNumbers] = useState(null);
  const [lastProductUpdatedIndex, setLastProductUpdatedIndex] = useState(null);
  const [lastProductFinishedIndex, setLastProductFinishedIndex] = useState(null);
  const [checkoutValues, setCheckoutValues] = useState({});
  const [fileList, setFileList] = useState([]);
  const [products, setProducts] = useState([
    {
      id: 1,
      department: null,
      productType: null,
      visible: true,
    },
  ]);
  const [dataModalVisible, setDataModalVisible] = useState(false);
  const [showingPickUpInStore, setShowingPickupInStore] = useState(false);
  const [showingDropoffModal, setShowingDropoffModal] = useState(false);
  const [productsModalVisible, setProductsModalVisible] = useState(false);
  const [clickedDropoffSearch, setClickedDropoffSearch] = useState(false);
  const [takebackCredit, setTakebackCredit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingError, setUploadingError] = useState([null, null]);
  const isMobileOrTablet = useIsMobile('1200px');
  const [CheckoutForm] = Form.useForm();

  const market = getMarket(locale);

  useEffect(() => {
    if (currentStep === STEPS.CHOOSE_DEPT_AND_PRODUCT && products.length <= 3) {
      document.querySelector('#choose-dept-1').scrollIntoView({ behavior: 'smooth' });
    }
    if (currentStep === STEPS.CHOOSE_DEPT_AND_PRODUCT && products.length > 3) {
      document.querySelector(`#choose-dept-${lastProductUpdatedIndex + 2}`).scrollIntoView({ behavior: 'smooth' });
    }
    if (currentStep === STEPS.CHOOSE_PRODUCT) {
      const index = products.length <= 3 ? lastProductUpdatedIndex + 1 : lastProductUpdatedIndex + 2;
      document.querySelector(`#choose-product-${index}`).scrollIntoView({ behavior: 'smooth' });
    }
    if (currentStep === STEPS.CHECKOUT_FORM) {
      document.querySelector('#checkout-form').scrollIntoView({ behavior: 'smooth' });
    }
    if (currentStep === STEPS.CHOOSE_ADD_OR_CHECKOUT) {
      document.querySelector('#choose-add-or-checkout').scrollIntoView({ behavior: 'smooth' });
    }
    if (currentStep === STEPS.CHOOSE_RETURN_METHOD) {
      document.querySelector('#return-method-form').scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentStep]);

  useEffect(() => {
    if (pairNumbers) {
      const newProducts = [
        {
          id: 1,
          department: null,
          productType: null,
          visible: true,
        },
      ];
      if (pairNumbers !== 'one') {
        newProducts.push({
          id: 2,
          department: null,
          productType: null,
          visible: false,
        });
        newProducts.push({
          id: 3,
          department: null,
          productType: null,
          visible: false,
        });
      }
      setCurrentStep(STEPS.CHOOSE_DEPT_AND_PRODUCT);
      setProducts(newProducts);
      setLastProductUpdatedIndex(null);
      setLastProductFinishedIndex(null);
      document.querySelector('#choose-dept-1').scrollIntoView({ behavior: 'smooth' });
    }
  }, [pairNumbers]);

  useEffect(() => {
    if (lastProductUpdatedIndex !== null && products[lastProductUpdatedIndex].department && products[lastProductUpdatedIndex].productType) {
      document.querySelector(`#pair-story-${lastProductUpdatedIndex + 1}`).scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastProductUpdatedIndex]);
  useEffect(() => {
    if (lastProductFinishedIndex !== null && products[lastProductFinishedIndex + 1].visible) {
      document.querySelector(`#choose-dept-${lastProductFinishedIndex + 2}`).scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastProductFinishedIndex]);

  useEffect(() => {
    const getTakebackCreditHandler = async () => {
      const finishedFormProducts = products.filter((product) => product.department !== null && product.productType !== null);
      if (finishedFormProducts.length > 0 && !takebackCredit) {
        const creditRs = await getTakebackCredit(locale);
        setTakebackCredit(creditRs);
      }
    };

    getTakebackCreditHandler();
  }, [products]);

  const handleSetPairNumbers = (data) => {
    setPairNumbers(data.value);
    if (data.value === pairNumbers) {
      document.querySelector('#choose-dept-1').scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleUpdateProductData = (data) => {
    const isLesserThanDesktopSize = window.innerWidth <= 1200;
    const newProducts = JSON.parse(JSON.stringify(products)).map((product, index) => {
      if (index === data.index) {
        product[data.groupId] = data.value;
      }
      return product;
    });
    setProducts(newProducts);
    if (newProducts[data.index].department && newProducts[data.index].productType) {
      setLastProductUpdatedIndex(data.index);
      if (data.index === lastProductUpdatedIndex) {
        document.querySelector(`#pair-story-${lastProductUpdatedIndex + 1}`).scrollIntoView({ behavior: 'smooth' });
      }
    }
    if (isLesserThanDesktopSize && newProducts[data.index].department && !newProducts[data.index].productType) {
      // esta en mobile/tablet y ha elegido departamento pero aun no ha elegido producto porque es un paso "adicional", asi que mostramos ese paso extra
      if (currentStep !== STEPS.CHOOSE_PRODUCT) {
        setCurrentStep(STEPS.CHOOSE_PRODUCT);
      } else if (currentStep === STEPS.CHOOSE_PRODUCT && pairNumbers !== 'one' && products.length <= 3) {
        /* solucion temporal y poco elegante: meterlo dentro del setTimeout pero es para asegurarme que el scroll se produce despues del setProducts.
        como solo pasa en este caso no queria meter un useEffect para comprobar el products y la logica de saber si tengo que hacer este scroll en concreto
        ya que es algo un poco tedioso y solo ocurre cuando no estas en desktop por el paso "extra" que se añade de elegir producto */
        setTimeout(() => {
          document.querySelector(`#choose-product-${data.index + 1}`).scrollIntoView({ behavior: 'smooth' });
        });
      }
    }
  };

  const handleOnClickNextSkipBtn = (index) => {
    if (pairNumbers === 'one' || index + 1 === PAIRS_LIMIT) {
      if (currentStep === STEPS.CHECKOUT_FORM) {
        document.querySelector('#checkout-form').scrollIntoView({ behavior: 'smooth' });
      } else {
        setCurrentStep(STEPS.CHECKOUT_FORM);
      }
    } else {
      const isLastProduct = index + 1 === products.length - 1;
      // products.length - 1 es menos uno porque el ultimo producto del array siempre es "ficticio" hasta que lo añadan, para poder hacer bien el efecto del scroll
      if (isLastProduct) {
        if (currentStep === STEPS.CHOOSE_ADD_OR_CHECKOUT) {
          document.querySelector('#choose-add-or-checkout').scrollIntoView({ behavior: 'smooth' });
        }
        setCurrentStep(STEPS.CHOOSE_ADD_OR_CHECKOUT);
      } else {
        const newProducts = JSON.parse(JSON.stringify(products)).map((product, productIndex) => {
          if (productIndex === index + 1) {
            product.visible = true;
          }
          return product;
        });
        setProducts(newProducts);
        setLastProductFinishedIndex(index);
        if (lastProductFinishedIndex === index) {
          document.querySelector(`#choose-dept-${lastProductFinishedIndex + 2}`).scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleAddAnotherPair = () => {
    if (products.length - 1 < PAIRS_LIMIT) {
      // const isLesserThanDesktopSize = window.innerWidth <= 1200;
      const newLastProductFinishedIndex = lastProductUpdatedIndex + 1;
      const newProducts = JSON.parse(JSON.stringify(products));
      newProducts[newProducts.length - 1].visible = true;
      newProducts.push({
        id: newProducts.length + 1,
        department: null,
        productType: null,
        visible: false,
      });
      setProducts(newProducts);
      setLastProductFinishedIndex(newLastProductFinishedIndex);
      setCurrentStep(STEPS.CHOOSE_DEPT_AND_PRODUCT);
    } else {
      console.log('FULL PAIRS');
    }
  };

  const handleContinueCheckout = () => {
    if (currentStep === STEPS.CHECKOUT_FORM) {
      document.querySelector('#checkout-form').scrollIntoView({ behavior: 'smooth' });
    }
    setCurrentStep(STEPS.CHECKOUT_FORM);
  };

  const onFinishCheckoutForm = async (values) => {
    if (currentStep === STEPS.CHOOSE_RETURN_METHOD) {
      document.querySelector('#return-method-form').scrollIntoView({ behavior: 'smooth' });
    }
    setCheckoutValues(values);
    setCurrentStep(STEPS.CHOOSE_RETURN_METHOD);
  };

  const onFinishFailedCheckoutForm = (errorInfo) => {
    console.log('Failed:', errorInfo);
    setCurrentStep(STEPS.CHECKOUT_FORM);
  };

  const onSelectReturnMethod = async (returnObject) => {
    setLoading(true);
    try {
      const formData = new FormData();
      fileList.forEach((file) => {
        formData.append('files', file);
      });
      const pairsArray = products
        .filter((product) => product.visible)
        .map((product, index) => ({
          category: product.department,
          type: product.productType,
          // size: 42,
          story: document.querySelector(`#input-story-${product.id}`).value || '',
          imagesName: [fileList?.[index]?.name || ''],
        }));
      const requestObject = {
        pairs: pairsArray,
        address: {
          name: checkoutValues.name,
          surname: checkoutValues.surname,
          email: checkoutValues.mail,
          countryId: market,
          // city: 'test',
          // telephone: '971971971',
        },
        returnMethod: returnObject.returnMethod,
      };
      if (returnObject.returnMethod === RETURN_TO_STORE) {
        requestObject.returnStoreId = returnObject.id;
      }
      if (store) {
        requestObject.originStoreId = store;
      }
      const appIpad = getCookie(COOKIES.APP_IPAD) || '';
      const tpvStoreId = getCookie(COOKIES.TPV_STORE_ID) || '';
      if (appIpad === 'true' && tpvStoreId.length > 0) {
        requestObject.originStoreId = tpvStoreId;
      }

      formData.append('request', JSON.stringify(requestObject));
      formData.append('profile', locale);
      formData.append('lab', getDomainValue({ camper: false, nnormal: false, camperlab: true }));

      const rq = await postTakeback(formData);
      if (rq.reference && typeof rq.reference === 'string' && rq.reference.length > 0) {
        window.location.assign(`/${locale}/takeback/success?reference=${rq.reference}`);
        setLoading(false);
      } else {
        Modal.error({
          title: t('takeback', 'error.titulo', 'WE’RE IN A TANGLE'),
          content: t(
            'takeback',
            'error.descripcion',
            'Our server has developed some unexpected knots (that’s what happens when you work with so many lace-up shoes). Could you come back later to see if our engineers have managed to unravel them? Thank you; we’re sorry for the inconvenience',
          ),
        });
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (returnObject.returnMethod === RETURN_TO_STORE) {
        returnObject.loader(false);
      }
      Modal.error({
        title: t('takeback', 'error.titulo', 'WE’RE IN A TANGLE'),
        content: t(
          'takeback',
          'error.descripcion',
          'Our server has developed some unexpected knots (that’s what happens when you work with so many lace-up shoes). Could you come back later to see if our engineers have managed to unravel them? Thank you; we’re sorry for the inconvenience',
        ),
      });
    }
  };

  const handleGoToFindStore = () => {
    setClickedDropoffSearch(false);
    setShowingDropoffModal(false);
    setShowingPickupInStore(true);
  };

  const handleGoToFindDropoff = () => {
    setClickedDropoffSearch(false);
    setShowingDropoffModal(true);
    setShowingPickupInStore(false);
  };

  const beforeUpload = (file, index) => {
    const newFileList = [...fileList];
    newFileList[index] = file;
    setFileList(newFileList);

    // no funciona esto, al pasarle false según la docu debería prevenir la subida
    // pero no previene nada
    // vemos que poniendo false como return directamente en la prop funciona
    return false;
  };

  const onRemove = (file, index) => {
    // const index = fileList.indexOf(file);
    let newFileList = [...fileList];
    newFileList = newFileList.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  const finishedFormProducts = products.filter((product) => product.department !== null && product.productType !== null);
  const departmentButtons = [
    { value: 'women', src: '/assets-new/takeback/women_outline.png', text: t('takeback', 'women', 'Women') },
    { value: 'men', src: '/assets-new/takeback/men_outline.png', text: t('takeback', 'men', 'Men') },
    { value: 'kids', src: '/assets-new/takeback/kids_outline.png', text: t('takeback', 'kids', 'Kids') },
  ];
  const productTypeButtons = [
    { value: 'sneaker', src: '/assets-new/takeback/Sneaker_outline.png', text: t('takeback', 'sneakers', 'Sneakers') },
    { value: 'sandal', src: '/assets-new/takeback/Sandal_outline.png', text: t('takeback', 'sandals', 'Sandals') },
    { value: 'heel', src: '/assets-new/takeback/heel_outline_2.png', text: t('takeback', 'heels', 'Heels') },
    { value: 'formal', src: '/assets-new/takeback/Formal_outline.png', text: t('takeback', 'formal', 'Formal') },
    { value: 'slipper', src: '/assets-new/takeback/Slipper_outline.png', text: t('takeback', 'slippers', 'Slippers') },
    { value: 'boot', src: '/assets-new/takeback/Boot_outline.png', text: t('takeback', 'boots', 'Boots') },
  ];
  const uploadClassname = [styles.uploadWrapper0, styles.uploadWrapper1, styles.uploadWrapper2, styles.uploadWrapper3];

  let priceTag = '';

  if (takebackCredit !== null) {
    const priceText = finishedFormProducts.length === 1 ? takebackCredit?.creditOnePairFormatted : takebackCredit?.creditMorePairsFormatted;
    priceTag = `- ${priceText}`;
  }

  const handleUploadError = (index, value) => {
    const newValues = [...uploadingError];
    newValues[index] = value;
    setUploadingError(newValues);
  };

  return (
    <Layout {...defaultProps} extratags={[<link key="canonical" rel="canonical" href={`${baseUrl}/${locale}/no_trace_program`} />]} pageName="takeback">
      <div className={styles.container}>
        <HeadSEO title="Takeback" description="Takeback Service" />
        <main className={styles.main}>
          <div className={styles.header}>
            <h1>{t('takeback', 'header.title', 'Tell Us About Your Shoes')}</h1>
            <p>{t('takeback', 'header.description', 'Please tell us a little bit about each pair you will be sending.')}</p>
          </div>
          <div className={`${styles.choosePairForm} ${styles.sectionOdd}`}>
            <TakebackSelectGroup
              title={t('takeback', 'choose.num.pairs', 'Choose your number of pairs')}
              buttons={[
                { value: 'one', src: '/assets-new/takeback/1_pair_outline.png', text: t('takeback', 'one.pair', '1 Pair') },
                { value: 'more', src: '/assets-new/takeback/2_pairs_outline.png', text: t('takeback', 'two.or.more.pair', '2 pairs or more') },
              ]}
              maxColsMd={2}
              active={pairNumbers}
              setter={handleSetPairNumbers}
              id={'pairNumbers'}
            />
          </div>
          {products.map((product, index) => {
            const isShowing = pairNumbers === 'one' ? true : product.visible;
            const wizardClassname =
              currentStep >= STEPS.CHOOSE_DEPT_AND_PRODUCT && isShowing ?
                `${styles.chooseDepartmentForm} ${styles.chooseDepartmentFormActive} ${index === 0 || !isMobileOrTablet ? styles.sectionEven : styles.sectionOdd}`
              : styles.chooseDepartmentForm;
            const productWizardClassname =
              currentStep >= STEPS.CHOOSE_PRODUCT && isShowing && product.department !== null ?
                `${styles.chooseDepartmentForm} ${styles.chooseDepartmentFormActive} ${index === 0 ? styles.sectionOdd : styles.sectionEven}`
              : styles.chooseDepartmentForm;
            return (
              <Fragment key={product.id}>
                <div id={`choose-dept-${product.id}`} className={wizardClassname}>
                  <TakebackModalTrigger count={finishedFormProducts.length} onClick={() => setProductsModalVisible(true)} />
                  <div className={styles.deptAndProductRow}>
                    <TakebackSelectGroup
                      title={t('takeback', 'choose.department', 'Choose department')}
                      buttons={departmentButtons}
                      maxColsMd={3}
                      active={product.department}
                      setter={handleUpdateProductData}
                      id="department"
                      index={index}
                    />
                    <TakebackSelectGroup
                      className={styles.hiddenLesserThanDesktop}
                      title={t('takeback', 'choose.product', 'Choose product type ')}
                      buttons={productTypeButtons}
                      maxColsXs={2}
                      maxColsMd={3}
                      active={product.productType}
                      setter={handleUpdateProductData}
                      id="productType"
                      index={index}
                    />
                  </div>
                  <Progress
                    className={`${currentStep >= STEPS.CHOOSE_DEPT_AND_PRODUCT && isShowing ? styles.progressActive : styles.progressHidden} ${styles.progress}`}
                    percent={product.department && product.productType ? 33 : 0}
                    format={() => `${t('takeback', 'pair', 'Pair')} ${product.id} ${priceTag}`}
                    strokeColor="#000000"
                    strokeWidth={40}
                  />
                </div>

                <div id={`choose-product-${product.id}`} className={`${productWizardClassname} ${styles.productSection}`}>
                  <TakebackModalTrigger count={finishedFormProducts.length} onClick={() => setProductsModalVisible(true)} />
                  <div className={styles.deptAndProductRow}>
                    <TakebackSelectGroup
                      title={t('takeback', 'choose.product', 'Choose product type ')}
                      buttons={productTypeButtons}
                      maxColsXs={2}
                      maxColsMd={3}
                      active={product.productType}
                      setter={handleUpdateProductData}
                      id="productType"
                      index={index}
                    />
                  </div>
                  <Progress
                    className={`${currentStep >= STEPS.CHOOSE_PRODUCT && isShowing && product.department !== null ? `${styles.progressActive} ${styles.productSection}` : styles.progressHidden} ${styles.progress} ${styles.productSection}`}
                    percent={product.department && product.productType ? 33 : 0}
                    format={() => `${t('takeback', 'pair', 'Pair')} ${product.id} ${priceTag}`}
                    strokeColor="#000000"
                    strokeWidth={40}
                  />
                </div>
                <div
                  id={`pair-story-${product.id}`}
                  className={
                    product.department && product.productType ?
                      `${styles.tellUsStoryForm} ${styles.tellUsStoryFormActive} ${isMobileOrTablet && index === 0 ? styles.sectionEven : styles.sectionOdd}`
                    : styles.tellUsStoryForm
                  }
                >
                  <TakebackModalTrigger count={finishedFormProducts.length} onClick={() => setProductsModalVisible(true)} />
                  <Row justify="space-around" align="middle" className={styles.storyFormRow}>
                    <Col xs={20} md={12} xl={8}>
                      <p className={styles.tellUsStoryTitle}>
                        {t('takeback', 'tell.story', 'We invite you to tell the unique story behind this pair (optional):')}
                      </p>
                      <div className={styles.storyInputDiv}>
                        <TextArea id={`input-story-${product.id}`} rows={4} maxLength={500} style={{ height: 160, resize: 'none' }} />
                        <p>
                          <small>{t('takeback', 'tell.story.input.title', 'Tell us your story (max 500 characters)')}</small>
                        </p>
                      </div>
                      <p>
                        <small>{t('takeback', 'tell.story.input.description', 'Selected stories may be seen on Camper.com')}</small>
                      </p>
                      <p className={styles.tellUsStoryDescription}>{t('takeback', 'tell.story.upload.title', 'Share an image of your shoes:')}</p>
                      <Upload
                        className={`${styles.uploadWrapper} ${uploadClassname[index]}`}
                        maxCount={1}
                        name={`upload-image-${product.id}`}
                        fileList={fileList}
                        beforeUpload={(file) => {
                          // comentar magia negra antd
                          if (file.size > 1000000) {
                            handleUploadError(index, true);
                            return false;
                          }

                          handleUploadError(index, false);
                          return beforeUpload(file, index);
                        }}
                        onRemove={(file) => onRemove(file, index)}
                      >
                        <Button className={styles.uploadBtn} size="large" type="primary" icon={<UploadTakebackSVG />}>
                          {t('takeback', 'upload.image', 'Upload Image')}
                        </Button>
                      </Upload>
                      <p className={`${styles.tellUsStoryDescription} ${styles.imageDescriptionText}`}>
                        <small>{t('takeback', 'upload.image.description', 'Recognized image formats less than XX mb accepted')}</small>
                      </p>
                      {uploadingError[index] && <p className="error">{t('takeback', 'upload.image.error', '***The image is too large to be uploaded.')}</p>}
                      <Button className={styles.nextSkipBtn} type="primary" onClick={() => handleOnClickNextSkipBtn(index)}>
                        {uploadingError[index] === true || uploadingError[index] === null ? t('takeback', 'skip', 'Skip') : t('takeback', 'next', 'Next')}
                      </Button>
                    </Col>
                  </Row>
                  <Progress
                    className={`${product.department && product.productType ? styles.progressActive : styles.progressHidden} ${styles.progress} ${styles.progressTwoThirds}`}
                    percent={66}
                    format={() => `${t('takeback', 'pair', 'Pair')} ${product.id} ${priceTag}`}
                    strokeColor="#000000"
                    strokeWidth={40}
                  />
                </div>
              </Fragment>
            );
          })}
          <div
            id="choose-add-or-checkout"
            className={
              currentStep >= STEPS.CHOOSE_ADD_OR_CHECKOUT && pairNumbers !== 'one' && products.length - 1 < PAIRS_LIMIT ?
                `${styles.chooseAddOrCheckout} ${styles.chooseAddOrCheckoutActive} ${styles.sectionEven}`
              : styles.chooseAddOrCheckout
            }
          >
            <TakebackModalTrigger count={finishedFormProducts.length} onClick={() => setProductsModalVisible(true)} />
            <div className={styles.chooseAddOrCheckoutBtnGroup}>
              <Button icon={<AddMoreSVG />} className={`${styles.uploadBtn} ${styles.addMoreBtn}`} type="primary" onClick={handleAddAnotherPair}>
                {t('takeback', 'add.another.pair', 'Add Another Pair')}
              </Button>
              <span>{t('takeback', 'or', 'Or')}</span>
              <Button
                className={`${styles.nextSkipBtn} ${styles.returnMethodBtn} ${styles.continueCheckoutBtn}`}
                type="primary"
                onClick={handleContinueCheckout}
              >
                {t('takeback', 'continue.to.checkout', 'Continue to checkout')}
              </Button>
            </div>
          </div>
          <div
            id="checkout-form"
            className={
              currentStep >= STEPS.CHECKOUT_FORM ?
                `${styles.checkoutFormWrapper} ${styles.checkoutFormWrapperActive} ${pairNumbers === 'one' && !isMobileOrTablet ? styles.sectionEven : styles.sectionOdd}`
              : styles.checkoutFormWrapper
            }
          >
            <TakebackModalTrigger count={finishedFormProducts.length} onClick={() => setProductsModalVisible(true)} />
            <Row justify="space-around" align="middle" className={styles.checkoutFormRow}>
              <Col xs={20} md={12} xl={8}>
                <p className={styles.tellUsStoryTitle}>
                  {t('takeback', 'form.checkout.title', 'Join The Walking Society and let us know where we should send your Camper credit.')}
                </p>
                <Form
                  form={CheckoutForm}
                  layout="vertical"
                  initialValues={{
                    name: '',
                    surname: '',
                    email: '',
                  }}
                  name="register"
                  onFinish={onFinishCheckoutForm}
                  onFinishFailed={onFinishFailedCheckoutForm}
                  requiredMark="optional"
                  scrollToFirstError
                  autoComplete="true"
                  className={`${styles.checkoutForm} customForm customFormTransparent`}
                >
                  <Row gutter={[0, 0]}>
                    <Col xs={24}>
                      <FormInput name="name" rules={validate('name')} floatLabel={t('takeback', 'name', 'Name')} required>
                        <Input tabIndex="1" placeholder="" />
                      </FormInput>
                      <FormInput name="surname" rules={validate('surname')} floatLabel={t('takeback', 'surname', 'Surname')} required>
                        <Input tabIndex="2" placeholder="" />
                      </FormInput>
                      <FormInput name="mail" rules={validate('email')} floatLabel={t('takeback', 'email', 'E-mail')} required>
                        <Input tabIndex="3" placeholder="" type="email" formNoValidate />
                      </FormInput>
                      <p onClick={() => setDataModalVisible(true)} className={styles.dataProtection}>
                        {t('menu.derecha', 'informacion.proteccion.datos')}
                      </p>
                      <Form.Item
                        valuePropName="checked"
                        className={`${styles.checkboxRow} privacyPolicy`}
                        name="privacyPolicy"
                        rules={validate('privacyPolicy')}
                      >
                        <Checkbox tabIndex="4">
                          {locale === 'ko_KR' ?
                            <span className={styles.checkboxLabel}>
                              <a tabIndex="5" href={profileData?.urlPrivacyPolicy} target="_blank" rel="noreferrer">
                                {t('takeback', 'condiciones.generales')}
                              </a>
                              &nbsp;
                              {t('takeback', 'y.la')}&nbsp;
                              <a tabIndex="6" href={profileData?.urlPrivacyPolicy || '#'} target="_blank" rel="noreferrer">
                                {t('takeback', 'politica.privacidad')}
                              </a>
                              {t('takeback', 'acepto.las')}&nbsp;
                            </span>
                          : country === 'US' ?
                            <TakebackTermsAndConditionsText className={styles.checkboxLabel} />
                          : <span className={styles.checkboxLabel}>
                              {t('takeback', 'acepto.las')}&nbsp;
                              <a tabIndex="5" href={profileData?.urlPrivacyPolicy} target="_blank" rel="noreferrer">
                                {t('takeback', 'condiciones.generales')}
                              </a>
                              &nbsp;
                              {t('takeback', 'y.la')}&nbsp;
                              <a tabIndex="6" href={`/html/legal/takeback/TC_TB_${getLang(locale).toUpperCase()}.pdf`} target="_blank" rel="noreferrer">
                                {t('takeback', 'politica.privacidad')}
                              </a>
                              {market === 'IT' && ` ${t('compra.formulario', 'cond.italia')}`}
                            </span>
                          }
                        </Checkbox>
                      </Form.Item>
                      {market === 'ES' && <p className={styles.excludedLabel}>{t('takeback', 'excluidos.ES', '**Queda excluido Canarias, Ceuta y Melilla')}</p>}
                      <Form.Item className={styles.submitCheckoutBtn}>
                        <Button className={`${styles.nextSkipBtn} ${styles.submitCheckoutBtn}`} tabIndex="9" type="primary" htmlType="submit" loading={loading}>
                          {t('takeback', 'confirm', 'Confirm')}
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </div>
          <div
            id="return-method-form"
            className={
              currentStep >= STEPS.CHOOSE_RETURN_METHOD ?
                `${styles.tellUsStoryForm} ${styles.returnMethodForm} ${styles.tellUsStoryFormActive} ${pairNumbers === 'one' && !isMobileOrTablet ? styles.sectionOdd : styles.sectionEven}`
              : styles.tellUsStoryForm
            }
          >
            <TakebackModalTrigger count={finishedFormProducts.length} onClick={() => setProductsModalVisible(true)} />
            <div className={styles.returnMethodFormContent}>
              <p className={styles.tellUsStoryTitle}>{t('takeback', 'choose.return.mehtod', 'Choose your preferred return option:')}</p>
              <Row className={styles.rowReturnMethod}>
                {!RETURN_IN_STORE_DISABLED.includes(market) && (
                  <TakebackIconButton
                    active="in-store"
                    value="in-store"
                    alt="in store image"
                    setter={() => setShowingPickupInStore(true)}
                    src="/assets-new/takeback/in-store-outline.png"
                    text={t('takeback', 'in.store', 'In-Store')}
                    md={12}
                  >
                    <>
                      <span className={styles.returnMethodDescription}>
                        {t(
                          'takeback',
                          'in.store.description',
                          'Leave your shoes with a staff member at any Camper store to receive credit toward your next purchase.',
                        )}
                      </span>
                      <Button className={`${styles.nextSkipBtn} ${styles.returnMethodBtn}`} type="primary" onClick={() => setShowingPickupInStore(true)}>
                        {t('takeback', 'find.your.store', 'Find Your Store')}
                      </Button>
                    </>
                  </TakebackIconButton>
                )}
                {!RETURN_IN_DROPOFF_DISABLED.includes(market) && (
                  <TakebackIconButton
                    active="drop-off"
                    value="drop-off"
                    alt="drop-off image"
                    setter={() => setShowingDropoffModal(true)}
                    src="/assets-new/takeback/drop-off-outline.png"
                    text={t('takeback', 'drop.off', 'Dropp-off Point')}
                    md={12}
                  >
                    <>
                      <span className={styles.returnMethodDescription}>
                        {t(
                          'takeback',
                          'drop.off.description',
                          'Attach a prepaid shipping label to your package and leave it at your most convenient drop-off point.',
                        )}
                      </span>
                      <Button className={`${styles.nextSkipBtn} ${styles.returnMethodBtn}`} type="primary" onClick={() => setShowingDropoffModal(true)}>
                        {t('takeback', 'find.your.drop.off', 'Find Location')}
                      </Button>
                    </>
                  </TakebackIconButton>
                )}
              </Row>
            </div>
          </div>
          <ProtectionDataModal visible={dataModalVisible} onCancel={() => setDataModalVisible(false)} origin="newsletter" />
          <DispoModal
            productSheet={{
              code: '17665-011',
              locale,
              nameBase: '',
              sizes: [],
            }}
            visible={showingPickUpInStore}
            onClose={() => setShowingPickupInStore(false)}
            takeback
            onClickSearchDropoff={handleGoToFindDropoff}
            onSelectStore={onSelectReturnMethod}
          />
          <Modal width={'400px'} visible={showingDropoffModal} footer={null} onCancel={() => setShowingDropoffModal(false)} closeIcon={<CloseModalSVG />}>
            <div className={styles.dropoffModalContent}>
              {!clickedDropoffSearch ?
                <>
                  <h2 className={styles.modalTitle}>{t('takeback', 'dropoff.points', 'Drop-Off Points')}</h2>
                  <p>{t('takeback', 'search.dropoff.points', 'Search your nearest Drop-Off Point:')}</p>
                  <DropoffPointButton
                    onClick={() => setClickedDropoffSearch(true)}
                    className={styles.nextSkipBtn}
                    market={market}
                    label={t('takeback', 'search', 'Search')}
                    type="primary"
                    droppointUrl={droppointUrl}
                  />
                  <p>{t('takeback', 'prefer.camper.store', 'Prefer a Camper store instead?')}</p>
                  <Button onClick={handleGoToFindStore} className={styles.secondaryBtn} type="primary">
                    {t('takeback', 'find.my.store', 'Find My Store')}
                  </Button>
                </>
              : <>
                  <h2 className={styles.modalTitle}>{t('takeback', 'dropoff.point.satisfied', 'Are you satisfied with your nearest drop-off point?')}</h2>
                  <Button
                    loading={loading}
                    onClick={() => onSelectReturnMethod({ returnMethod: RETURN_TO_DROPOFF })}
                    className={styles.nextSkipBtn}
                    type="primary"
                  >
                    {t('takeback', 'yes', 'Yes')}
                  </Button>
                  <Button onClick={handleGoToFindStore} className={styles.secondaryBtn} type="primary">
                    {t('takeback', 'no.prefer.find.my.store', 'No, I prefer to visit a Camper store')}
                  </Button>
                </>
              }
            </div>
          </Modal>
          <Modal
            width={'400px'}
            visible={productsModalVisible}
            footer={
              <div className={styles.modalProductsFooter}>
                <span>{`${finishedFormProducts.length} ${finishedFormProducts.length === 1 ? t('takeback', 'pair', 'Pair') : t('takeback', 'pairs', 'Pairs')}`}</span>
                <span>{finishedFormProducts.length === 1 ? takebackCredit?.creditOnePairFormatted : takebackCredit?.creditMorePairsFormatted}</span>
              </div>
            }
            onCancel={() => setProductsModalVisible(false)}
            closeIcon={<CloseModalSVG />}
            className={styles.productsModal}
            style={{ minHeight: 560 }}
          >
            <h2 className={styles.modalTitle}>{t('takeback', 'your.products', 'Your Products')}</h2>
            <ul className={styles.productModalList}>
              {finishedFormProducts.map((product) => {
                const departmentBtn = departmentButtons.filter((button) => button.value === product.department)[0];
                const productTypeBtn = productTypeButtons.filter((button) => button.value === product.productType)[0];
                return (
                  <li key={product.id}>
                    <div>
                      <span>{`${t('takeback', 'pair', 'Pair')} ${product.id}`}</span>
                      <img src={productTypeBtn.src.replace('outline', 'fill')} alt={productTypeBtn.text} />
                    </div>
                    <span>{`${departmentBtn.text} + ${productTypeBtn.text}`}</span>
                  </li>
                );
              })}
            </ul>
          </Modal>
        </main>
      </div>
    </Layout>
  );
}

TakebackPage.propTypes = {
  tnmProps: any,
  footerProps: any,
  ribbon: any,
  store: string,
  droppointUrl: string,
};

TakebackPage.defaultProps = {
  tnmProps: null,
  footerProps: null,
  ribbon: null,
  store: null,
};

TakebackPage.i18nPage = 'Takeback';

export async function getServerSideProps(context) {
  const { locale, query } = context;
  const profileData = await checkProfile({ context, profile: locale });
  if (!IS_CAMPER || !profileData?.config?.take_back) {
    return {
      notFound: true,
    };
  }
  const { defaultProps } = await initCMS({ context, locale });
  const { store } = query;
  const droppointRs = await getDroppointsInfo(context, locale, 'takeback');

  return {
    props: {
      defaultProps,
      store: store || null,
      droppointUrl: droppointRs.droppointUrl || '#',
    },
  };
}

export default TakebackPage;
